import { useDataProvider, useNotify } from "ra-core";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { recordResult } from "./callPlan/useCallPlan";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  formatDate,
  labelOf,
  LOST_CHOICES,
  LOST_FOR_GOOD,
  LOST_REASONS,
  PAYMENT_METHODS,
  PLANS,
  PRODUCTS,
} from "./gulaConstants";
import { CASH_METHOD, isInCatalonia, paymentOptionsFor } from "./cashPayment";
import { fullName, type GulaClient } from "./gulaTypes";
import { Field, SimpleSelect } from "./SimpleSelect";
import { useAgents } from "./useAgents";

export type PendingMove = { client: GulaClient; toStage: string } | null;

/** YYYY-MM-DD of today + days (team's local time). */
const dayFromToday = (days: number) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const RECONTACT_QUICK_DAYS = [
  { label: "Mañana", days: 1 },
  { label: "Pasado mañana", days: 2 },
  { label: "En 3 días", days: 3 },
  { label: "En una semana", days: 7 },
];

/** What was left pending: the next caller knows what to do. */
const RECONTACT_REASONS = [
  "Le envío la info y le llamo",
  "Lo consulta en casa",
  "Me pide que le llame otro día",
  "Espero fotos o presupuesto",
];

const recontactNoteText = (day: string, reason: string, note: string) =>
  `Recontactar el ${new Date(`${day}T12:00:00`).toLocaleDateString("es-ES", {
    weekday: "long",
    day: "numeric",
    month: "long",
  })}: ${[reason, note.trim()].filter(Boolean).join(" · ")}`;

/** Note saved when a lead goes to "Compra segura" (shown on the card). */
const closingNoteText = (note: string) => `Nota de cierre: ${note.trim()}`;

/**
 * Asks for the extra data some moves need: a date to call back, the closing
 * note of a sure purchase, the reason a lead did not buy, or the order details
 * when the lead buys.
 */
export const StageChangeDialog = ({
  pending,
  onClose,
  onDone,
}: {
  pending: PendingMove;
  onClose: () => void;
  onDone: () => void;
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { meId } = useAgents();
  const [saving, setSaving] = useState(false);
  const [closingNote, setClosingNote] = useState("");
  const [recontactAt, setRecontactAt] = useState(dayFromToday(1));
  const [recontactReason, setRecontactReason] = useState("");
  const [recontactNote, setRecontactNote] = useState("");
  const [lostReason, setLostReason] = useState<string>("");
  const [lostNote, setLostNote] = useState("");
  const [product, setProduct] = useState(PRODUCTS[0].value);
  const [plan, setPlan] = useState("none");
  const [price, setPrice] = useState(String(PRODUCTS[0].price));
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [isPaid, setIsPaid] = useState(true);
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");
  const [province, setProvince] = useState("");
  const [notes, setNotes] = useState("");

  const client = pending?.client;
  useEffect(() => {
    if (!client) return;
    setAddress(client.address ?? "");
    setPostalCode(client.postal_code ?? "");
    setCity(client.city ?? "");
    setProvince(client.province ?? "");
    setNotes("");
    setClosingNote("");
    setRecontactAt(dayFromToday(1));
    setRecontactReason("");
    setRecontactNote("");
    setLostReason("");
    setLostNote("");
  }, [client]);

  if (!pending || !client) return null;
  const { toStage } = pending;
  const inCatalonia = isInCatalonia(postalCode, client.zone);
  const isCash = paymentMethod === CASH_METHOD;

  const choosePaymentMethod = (method: string) => {
    setPaymentMethod(method);
    // Cash is collected on installation day, not when the sale is made
    if (method === CASH_METHOD) setIsPaid(false);
  };

  const updateClient = (data: Record<string, unknown>) =>
    dataProvider.update("gula_clients", {
      id: client.id,
      data,
      previousData: { id: client.id },
    });

  const handleConfirm = async () => {
    setSaving(true);
    try {
      if (toStage === "recontact") {
        if (!recontactAt || recontactAt < dayFromToday(0)) {
          notify("Elige el día en que hay que volver a llamar", {
            type: "warning",
          });
          setSaving(false);
          return;
        }
        if (!recontactReason && !recontactNote.trim()) {
          notify("Di qué ha quedado pendiente para quien le llame", {
            type: "warning",
          });
          setSaving(false);
          return;
        }
        const again = client.stage === "recontact";
        await updateClient({ stage: toStage, recontact_at: recontactAt });
        // Already in Recontactar: the new date is a call too (counts and is
        // crossed out of today's list)
        if (again) await recordResult(client.id, "contacted");
        await dataProvider.create("contact_notes", {
          data: {
            contact_id: client.contact_id,
            text: recontactNoteText(
              recontactAt,
              recontactReason,
              recontactNote,
            ),
            date: new Date().toISOString(),
            sales_id: meId ?? null,
          },
        });
        notify(
          `${client.first_name ?? "Lead"}: recontactar el ${formatDate(recontactAt)}. Solo saldrá en las jornadas ese día`,
          { type: "success" },
        );
      } else if (toStage === "sure_purchase") {
        if (!closingNote.trim()) {
          notify("Escribe la nota de cierre", { type: "warning" });
          setSaving(false);
          return;
        }
        await updateClient({ stage: toStage, lost_reason: null });
        await dataProvider.create("contact_notes", {
          data: {
            contact_id: client.contact_id,
            text: closingNoteText(closingNote),
            date: new Date().toISOString(),
            sales_id: meId ?? null,
          },
        });
        notify(`${client.first_name ?? "Lead"} está en Cierre de compra`, {
          type: "success",
        });
      } else if (toStage === "no_purchase") {
        if (!lostReason) {
          notify("Elige el motivo", { type: "warning" });
          setSaving(false);
          return;
        }
        if (lostNote.trim().length < 8) {
          notify("Escribe qué te ha dicho: es lo que sirve para recuperarlo", {
            type: "warning",
          });
          setSaving(false);
          return;
        }
        const { data: result, error } = await getSupabaseClient().rpc(
          "gula_lost_lead",
          {
            p_client_id: Number(client.id),
            p_reason: lostReason,
            p_label: labelOf(LOST_REASONS, lostReason),
            p_note: lostNote.trim(),
          },
        );
        if (error) throw error;
        notify(
          result === "reactivation"
            ? `${client.first_name ?? "Lead"} pasa a Reactivar con su motivo: Mica y Joaquín lo recuperarán`
            : `${client.first_name ?? "Lead"} pasa a No compra`,
          { type: "success" },
        );
      } else if (toStage === "purchased") {
        if (!address || !postalCode || !city) {
          notify("Falta la dirección completa para preparar el envío", {
            type: "warning",
          });
          setSaving(false);
          return;
        }
        if (isCash && !inCatalonia) {
          notify("El pago en efectivo solo se puede hacer en Cataluña", {
            type: "warning",
          });
          setSaving(false);
          return;
        }
        await updateClient({
          stage: toStage,
          address,
          postal_code: postalCode,
          city,
          province,
          lost_reason: null,
        });
        const { data: fresh } = await dataProvider.getOne("gula_clients", {
          id: client.id,
        });
        await dataProvider.create("gula_orders", {
          data: {
            client_id: client.id,
            contact_id: client.contact_id,
            stage: "to_ship",
            product,
            plan,
            price: Number(price) || null,
            payment_method: paymentMethod,
            paid_at: isPaid ? new Date().toISOString() : null,
            installer_id: fresh?.installer_id ?? null,
            notes: notes || null,
          },
        });
        notify("Venta registrada: el pedido ya está en Operaciones", {
          type: "success",
        });
      }
      onDone();
    } catch (error) {
      console.error(error);
      notify("No se ha podido guardar", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const title =
    toStage === "recontact"
      ? "¿Cuándo hay que recontactar?"
      : toStage === "sure_purchase"
        ? "Cierre de compra: ¿cómo se cierra?"
        : toStage === "no_purchase"
          ? "¿Por qué no compra? Elige el motivo y cuéntalo"
          : "Registrar la venta";

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{fullName(client)}</DialogDescription>
        </DialogHeader>

        {toStage === "recontact" ? (
          <div className="flex flex-col gap-3">
            <Field label="¿Qué día hay que volver a llamar?">
              <div className="flex flex-wrap gap-1.5">
                {RECONTACT_QUICK_DAYS.map((option) => {
                  const day = dayFromToday(option.days);
                  return (
                    <button
                      key={option.days}
                      type="button"
                      onClick={() => setRecontactAt(day)}
                      className={cn(
                        "rounded-full border px-3 py-1.5 text-sm",
                        recontactAt === day
                          ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
                          : "hover:bg-muted",
                      )}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
              <Input
                type="date"
                min={dayFromToday(0)}
                value={recontactAt}
                onChange={(event) => setRecontactAt(event.target.value)}
              />
            </Field>
            <Field label="¿Qué ha quedado?">
              <div className="flex flex-wrap gap-1.5">
                {RECONTACT_REASONS.map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    onClick={() =>
                      setRecontactReason(
                        reason === recontactReason ? "" : reason,
                      )
                    }
                    className={cn(
                      "rounded-full border px-3 py-1.5 text-sm",
                      recontactReason === reason
                        ? "border-sky-500 bg-sky-50 text-sky-900 dark:bg-sky-400/15 dark:text-sky-100"
                        : "hover:bg-muted",
                    )}
                  >
                    {reason}
                  </button>
                ))}
              </div>
              <Textarea
                rows={2}
                value={recontactNote}
                onChange={(event) => setRecontactNote(event.target.value)}
                placeholder="Lo que tiene que saber quien le llame: dudas, hora que prefiere…"
              />
            </Field>
            <p className="text-xs text-muted-foreground">
              Hasta ese día no sale en ninguna jornada. Ese día aparece en
              «Recontactos de hoy».
            </p>
          </div>
        ) : null}

        {toStage === "sure_purchase" ? (
          <Field label="Nota de cierre">
            <Textarea
              autoFocus
              rows={4}
              value={closingNote}
              onChange={(event) => setClosingNote(event.target.value)}
              placeholder="Qué equipo quiere, cómo va a pagar, cuándo lo cierra…"
            />
          </Field>
        ) : null}

        {toStage === "no_purchase" ? (
          <div className="flex flex-col gap-3">
            {[
              {
                title: "Tiene ósmosis o no es cliente → No compra",
                reasons: LOST_CHOICES.filter((reason) =>
                  LOST_FOR_GOOD.includes(reason.value),
                ),
              },
              {
                title:
                  "No tiene ósmosis → pasa a Reactivar (lo recuperamos con contenido para su caso)",
                reasons: LOST_CHOICES.filter(
                  (reason) => !LOST_FOR_GOOD.includes(reason.value),
                ),
              },
            ].map((group) => (
              <div key={group.title} className="flex flex-col gap-1.5">
                <span className="text-xs font-medium text-muted-foreground">
                  {group.title}
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {group.reasons.map((reason) => (
                    <button
                      key={reason.value}
                      type="button"
                      onClick={() => setLostReason(reason.value)}
                      className={cn(
                        "rounded-full border px-3 py-1.5 text-left text-sm",
                        lostReason === reason.value
                          ? "border-neutral-900 bg-neutral-900 text-white dark:border-white dark:bg-white dark:text-neutral-900"
                          : "hover:bg-muted",
                      )}
                    >
                      {reason.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <Field label="Cuéntalo con tus palabras (obligatorio)">
              <Textarea
                rows={3}
                value={lostNote}
                onChange={(event) => setLostNote(event.target.value)}
                placeholder="Qué te ha dicho exactamente: sirve para saber cómo recuperarlo"
              />
            </Field>
          </div>
        ) : null}

        {toStage === "purchased" ? (
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <SimpleSelect
                label="Equipo"
                value={product}
                onChange={(value) => {
                  setProduct(value);
                  const match = PRODUCTS.find((item) => item.value === value);
                  if (match) setPrice(String(match.price));
                }}
                options={PRODUCTS}
              />
            </div>
            <SimpleSelect
              label="Plan de mantenimiento"
              value={plan}
              onChange={setPlan}
              options={PLANS}
            />
            <Field label="Precio (€)">
              <Input
                type="number"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />
            </Field>
            <div className="col-span-2">
              <SimpleSelect
                label="Forma de pago"
                value={paymentMethod}
                onChange={choosePaymentMethod}
                options={paymentOptionsFor(
                  PAYMENT_METHODS,
                  inCatalonia || isCash,
                )}
              />
              {isCash ? (
                <p
                  className={`mt-1 text-xs ${
                    inCatalonia
                      ? "text-muted-foreground"
                      : "font-medium text-destructive"
                  }`}
                >
                  {inCatalonia
                    ? "Solo Cataluña: se cobra en mano el día de la instalación."
                    : "El efectivo solo vale en Cataluña: elige otra forma de pago o revisa el código postal."}
                </p>
              ) : null}
            </div>
            <div className="col-span-2 flex items-center gap-2">
              <Checkbox
                id="is-paid"
                checked={isPaid}
                onCheckedChange={(checked) => setIsPaid(checked === true)}
              />
              <Label htmlFor="is-paid">Pago recibido</Label>
            </div>
            <div className="col-span-2">
              <Field label="Dirección de envío e instalación">
                <Input
                  value={address}
                  onChange={(event) => setAddress(event.target.value)}
                  placeholder="Calle, número, piso"
                />
              </Field>
            </div>
            <Field label="Código postal">
              <Input
                value={postalCode}
                onChange={(event) => setPostalCode(event.target.value)}
              />
            </Field>
            <Field label="Población">
              <Input
                value={city}
                onChange={(event) => setCity(event.target.value)}
              />
            </Field>
            <div className="col-span-2">
              <Field label="Provincia">
                <Input
                  value={province}
                  onChange={(event) => setProvince(event.target.value)}
                />
              </Field>
            </div>
            <div className="col-span-2">
              <Field label="Notas para el envío o la instalación">
                <Textarea
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                />
              </Field>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleConfirm} disabled={saving}>
            {saving ? "Guardando…" : "Confirmar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
